import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { contractNotesApi } from '../services/api';
import { ContractNote } from '../types/client';
import styles from './ContractNotes.module.css';
import { PageFrame, PageHeader, Surface, Button, SideTag, Note, Placeholder } from '@/components/ui';
import { formatNumber } from '@/utils/formatters';
import shared from '@/components/utils/shared.module.css';

const ContractNotesDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contractNote, setContractNote] = useState<ContractNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadContractNote(parseInt(id));
  }, [id]);

  const loadContractNote = async (noteId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await contractNotesApi.getContractNote(noteId);
      setContractNote(data);
    } catch (err: any) {
      console.error('Error loading contract note:', err);
      setError(err.response?.data?.message || 'Failed to load contract note details');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (value: number | undefined, decimals = 2): string => {
    if (value === undefined || value === null) return '—';
    return formatNumber(value, decimals);
  };

  const fmtDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  /* ── Loading ── */
  if (loading) {
    return (
      <PageFrame header={<PageHeader title="Contract Note Details" />}>
        <Surface>
          <Placeholder kind="loading" message="Loading contract note…" />
        </Surface>
      </PageFrame>
    );
  }

  /* ── Error / Not found ── */
  if (error || !contractNote) {
    return (
      <PageFrame
        header={
          <PageHeader
            title="Contract Note Details"
            actions={
              <Button onClick={() => navigate('/contract-notes')} icon={<ArrowLeft size={15} />}>
                Back to Contract Notes
              </Button>
            }
          />
        }
      >
        <Note tone="error">{error || 'Contract note not found'}</Note>
      </PageFrame>
    );
  }

  const isBuy = contractNote.sell_buy === 1;

  return (
    <PageFrame
      header={
        <PageHeader
          title="Contract Note Details"
          subtitle={`Contract No: ${contractNote.contract_no}`}
          actions={
            <>
              <SideTag side={isBuy ? 'buy' : 'sell'}>{contractNote.trade_type}</SideTag>
              <Button onClick={() => navigate('/contract-notes')} icon={<ArrowLeft size={15} />}>
                Back
              </Button>
            </>
          }
        />
      }
    >
      <Surface padded>
        <div className={styles['detail-body']}>

          {/* ── Row 1: Trade Info + Broker & Scheme ── */}
          <div className={styles['detail-top-grid']}>

            <div className={styles['detail-section']}>
              <h3 className={styles['detail-section-title']}>Trade Information</h3>
              <div className={`${styles['info-grid']} ${styles['info-grid-2']}`}>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Trade Date</span>
                  <span className={styles['info-value']}>{fmtDate(contractNote.trade_date)}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Trade Type</span>
                  <span className={styles['info-value']}>
                    <SideTag side={isBuy ? 'buy' : 'sell'}>{contractNote.trade_type}</SideTag>
                  </span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Settlement No</span>
                  <span className={styles['info-value']}>{contractNote.sett_no || '—'}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Settlement Type</span>
                  <span className={styles['info-value']}>{contractNote.sett_type || '—'}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Series</span>
                  <span className={styles['info-value']}>{contractNote.series || '—'}</span>
                </div>
              </div>
            </div>

            <div className={styles['detail-section']}>
              <h3 className={styles['detail-section-title']}>Broker & Scheme</h3>
              <div className={`${styles['info-grid']} ${styles['info-grid-2']}`}>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Broker Code</span>
                  <span className={styles['info-value']}>{contractNote.broker_code || '—'}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Broker Name</span>
                  <span className={styles['info-value']}>{contractNote.broker_name || '—'}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Scheme</span>
                  <span className={styles['info-value']}>{contractNote.scheme_code || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 2: Company Info ── */}
          <div className={styles['detail-section']}>
            <h3 className={styles['detail-section-title']}>Company Information</h3>
            <div className={`${styles['info-grid']} ${styles['info-grid-2']}`}>
              <div className={styles['info-field']}>
                <span className={styles['info-label']}>Symbol</span>
                <span className={styles['info-value']}>
                  <Link to={`/companies/${contractNote.company}`} className={styles.link}>
                    {contractNote.company_symbol}
                  </Link>
                </span>
              </div>
              <div className={styles['info-field']}>
                <span className={styles['info-label']}>Company Name</span>
                <span className={styles['info-value']}>{contractNote.company_name}</span>
              </div>
            </div>
          </div>

          {/* ── Row 3: Trade Details + Net Settlement side by side ── */}
          <div className={styles['detail-top-grid']}>

            <div className={styles['detail-section']}>
              <h3 className={styles['detail-section-title']}>Trade Details</h3>
              <div className={styles['info-grid']}>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Quantity</span>
                  <span className={styles['info-value']}>{fmt(contractNote.adj_quantity, 0)}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Market Rate</span>
                  <span className={styles['info-value']}>₹ {fmt(contractNote.adj_mkt_rate, 4)}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Market Amount</span>
                  <span className={styles['info-value']} style={{ fontWeight: 600 }}>
                    ₹ {fmt(contractNote.adj_mkt_amount, 2)}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles['detail-section']}>
              <h3 className={styles['detail-section-title']}>Net Settlement</h3>
              <div className={`${styles['info-grid']} ${styles['info-grid-2']}`}>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Net Rate</span>
                  <span className={styles['info-value']}>₹ {fmt(contractNote.adj_net_rate, 4)}</span>
                </div>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Net Amount</span>
                  <span className={styles['info-value-large']}>₹ {fmt(contractNote.adj_net_amt, 2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 4: Charges Breakdown ── */}
          <div className={styles['detail-section']}>
            <h3 className={styles['detail-section-title']}>Charges Breakdown</h3>
            <table className={`${shared.table} ${styles['charges-table']}`}>
              <tbody>
                <tr>
                  <td>Brokerage</td>
                  <td>₹ {fmt(contractNote.brokerage)}</td>
                </tr>
                <tr>
                  <td>STT (Securities Transaction Tax)</td>
                  <td>₹ {fmt(contractNote.stt)}</td>
                </tr>
                <tr>
                  <td>Service Tax</td>
                  <td>₹ {fmt(contractNote.ser_tax)}</td>
                </tr>
                <tr>
                  <td>SEBI Tax</td>
                  <td>₹ {fmt(contractNote.sebi_tax)}</td>
                </tr>
                <tr>
                  <td>Transaction Tax</td>
                  <td>₹ {fmt(contractNote.turn_tax)}</td>
                </tr>
                <tr>
                  <td>Stamp Duty</td>
                  <td>₹ {fmt(contractNote.stamp_duty)}</td>
                </tr>
                <tr>
                  <td>Total Charges</td>
                  <td>₹ {fmt(contractNote.total_charges)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Row 5: Audit Info ── */}
          {contractNote.created_ts && (
            <div className={styles['detail-section']}>
              <h3 className={styles['detail-section-title']}>Audit Information</h3>
              <div className={`${styles['info-grid']} ${styles['info-grid-2']}`}>
                <div className={styles['info-field']}>
                  <span className={styles['info-label']}>Created On</span>
                  <span className={styles['info-value']}>{fmtDate(contractNote.created_ts)}</span>
                </div>
                {contractNote.updated_ts && (
                  <div className={styles['info-field']}>
                    <span className={styles['info-label']}>Last Updated</span>
                    <span className={styles['info-value']}>{fmtDate(contractNote.updated_ts)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </Surface>
    </PageFrame>
  );
};

export default ContractNotesDetail;